import React, { useState, useRef } from 'react';

import { Button, Grid } from 'components/ui';
import Modal from 'components/Modal/Modal';
import FrictionlessAddressForm from 'components/Addresses/FrictionlessAddressForm';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import ErrorList from 'components/ErrorList';

import useAddressValidation from 'components/RichProfile/MyAccount/AutoReplenishment/ManageAddress/hooks/useAddressValidation';

import FormsUtils from 'utils/Forms';
import userUtils from 'utils/User';
import { wrapFunctionalComponent } from 'utils/framework';

function EditAddressModal({
    errorMessages = null,
    isOpen = false,
    localization = {},
    onAddressSelected = null,
    onBeforeValidation = null,
    onDismiss = null,
    selectedAddress = null,
    validateAddress = null
}) {
    const [isDefault, setIsDefault] = useState(selectedAddress?.isDefault ?? false);
    const [isManualEntry, setIsManualEntry] = useState(true);

    const addressFormRef = useRef();

    const shipCountry = userUtils.getShippingCountry().countryCode;

    const handleIsDefault = () => setIsDefault(prevIsDefault => !prevIsDefault);

    const { isHidden, validateAddressForm } = useAddressValidation({
        addressFormRef,
        isDefault,
        isManualEntry,
        onAddressSelected,
        onBeforeValidation,
        validateAddress
    });

    const onSubmitLoqateAddress = () => {
        setIsManualEntry(false);
    };

    const handleConfirm = e => {
        e?.preventDefault?.();

        const formInstance = addressFormRef?.current;

        if (!formInstance || typeof formInstance.getData !== 'function') {
            return;
        }

        const currentFormData = formInstance.getData()?.address;

        // Check if data has changed
        const hasChanges =
            currentFormData?.firstName !== selectedAddress?.firstName ||
            currentFormData?.lastName !== selectedAddress?.lastName ||
            currentFormData?.address1 !== selectedAddress?.address1 ||
            currentFormData?.address2 !== selectedAddress?.address2 ||
            currentFormData?.city !== selectedAddress?.city ||
            currentFormData?.state !== selectedAddress?.state ||
            currentFormData?.zipCode !== selectedAddress?.zipCode ||
            currentFormData?.country !== selectedAddress?.country ||
            currentFormData?.phoneNumber !== selectedAddress?.phoneNumber ||
            isDefault !== selectedAddress?.isDefault;

        if (!hasChanges) {
            // No changes detected, just close and return to saved addresses
            onDismiss?.();

            return;
        }

        // Changes detected, proceed with validation
        validateAddressForm(e);
    };

    return (
        <Modal
            isOpen={isOpen}
            isHidden={isHidden}
            onDismiss={onDismiss}
            hasBodyScroll={true}
            width={0}
            focusDialog={true}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title>{localization.modalTitle}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div id='ship_address_form'>
                    <ErrorList errorMessages={errorMessages} />
                    <FrictionlessAddressForm
                        editStore={FormsUtils.getStoreEditSectionName(FormsUtils.FORMS.CHECKOUT.SHIPPING_ADDRESS, false)}
                        isEditMode={!!selectedAddress}
                        address={selectedAddress}
                        country={selectedAddress?.country ?? shipCountry}
                        isCountryFieldHidden={true}
                        showAddress2Input={true}
                        onRef={comp => (addressFormRef.current = comp)}
                        isShortenedLoqateForm={false}
                        forceSingleColumn={true}
                        onSubmitLoqateAddress={onSubmitLoqateAddress}
                    />
                    <Checkbox
                        checked={isDefault}
                        onClick={handleIsDefault}
                        disabled={selectedAddress?.isDefault}
                        name='is_default'
                        marginBottom={4}
                    >
                        {localization.setAsDefault}
                    </Checkbox>
                </div>
            </Modal.Body>

            <Modal.Footer hasBorder={true}>
                <Grid
                    gap={4}
                    gridTemplateColumns={['1fr 1fr']}
                >
                    <Button
                        variant='secondary'
                        data-at={Sephora.debug.dataAt('address_edit_cancel')}
                        onClick={onDismiss}
                        children={localization.cancel}
                        minWidth={true}
                    />
                    <Button
                        variant='primary'
                        data-at={Sephora.debug.dataAt('address_edit_confirm')}
                        onClick={handleConfirm}
                        children={localization.confirm}
                    />
                </Grid>
            </Modal.Footer>
        </Modal>
    );
}

export default wrapFunctionalComponent(EditAddressModal, 'EditAddressModal');
